import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  SimpleGrid,
  useToast,
  Tooltip,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// Helper component for labels with info tooltip
function LabelWithTooltip({ children, tooltip }) {
  return (
    <HStack spacing={1} display="inline-flex">
      <span>{children}</span>
      {tooltip && (
        <Tooltip label={tooltip} hasArrow placement="top" fontSize="xs">
          <span>
            <Icon as={FontAwesomeIcon} icon={faInfoCircle} boxSize={3} color="gray.400" cursor="help" />
          </span>
        </Tooltip>
      )}
    </HStack>
  );
}

const initialFormData = {
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
  status: 'active',
};

function AdvertiserModal({ isOpen, onClose, onSubmit, advertiser, isLoading }) {
  const [formData, setFormData] = useState(initialFormData);
  const toast = useToast();
  const isEditing = !!advertiser;

  useEffect(() => {
    if (advertiser) {
      setFormData({
        name: advertiser.advertiser_name || '',
        contactName: advertiser.contact_name || '',
        contactEmail: advertiser.contact_email || '',
        contactPhone: advertiser.contact_phone || '',
        notes: advertiser.notes || '',
        status: advertiser.status || 'active',
      });
    } else {
      setFormData(initialFormData);
    }
  }, [advertiser, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Advertiser name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    await onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{isEditing ? 'Edit Advertiser' : 'New Advertiser'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>
                  <LabelWithTooltip tooltip="The company or brand name">
                    Advertiser Name
                  </LabelWithTooltip>
                </FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter advertiser name"
                  borderRadius="xl"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Primary contact person">
                      Contact Name
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="John Smith"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Contact email address">
                      Contact Email
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>
                    <LabelWithTooltip tooltip="Contact phone number">
                      Contact Phone
                    </LabelWithTooltip>
                  </FormLabel>
                  <Input
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    borderRadius="xl"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="paused">Paused</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>
                  <LabelWithTooltip tooltip="Internal notes about this advertiser">
                    Notes
                  </LabelWithTooltip>
                </FormLabel>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any internal notes..."
                  borderRadius="xl"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isLoading}
              borderRadius="xl"
            >
              {isEditing ? 'Save Changes' : 'Create Advertiser'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default AdvertiserModal;
